import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, parse, isValid } from 'date-fns';
import { TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import { DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers';
import InvisiblePopper from './styles/InvisiblePopper';
import InvisibleTextField from './styles/InvisibleTextField';

const EXPRESSIONS = ['{СЕГОДНЯ}', '{ТЕК_МЕС_НАЧ}', '{ТЕК_МЕС_КОН}', '{ПРЕД_МЕС_НАЧ}', '{ПРЕД_МЕС_КОН}', '{СЕЙЧАС}'];

function isValidDate(val) {
  if (!val) return false;

  const d = new Date(val);
  const isValidISODate = !Number.isNaN(d.getTime()) && d.getFullYear() < 10000;
  const isValidFormatedDate = isValid(parse(val, 'dd.MM.yyyy', new Date()));

  return isValidISODate || isValidFormatedDate;
}

function isValidExpression(val) {
  if (typeof val !== 'string') return false;

  const regex = new RegExp(`^(${EXPRESSIONS.join('|')})( *[+-] *\\d+)?$`);
  return regex.test(val.trim());
}

function isValidValue(val) {
  if (!val) return true;

  if (isValidDate(val)) return true;

  return isValidExpression(val);
}

function ddMMyyyytoISO(str) {
  if (!str) return null;
  const parsed = parse(str, 'dd.MM.yyyy', new Date());

  return isValid(parsed) ? parsed.toISOString() : null;
}

function AdvancedDatePicker({ label, value, from, name, onChange, required, error, dateOnly }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (isValidDate(value) && !isValidExpression(value)) {
      setInputValue(format(new Date(value), 'dd.MM.yyyy'));
    } else {
      setInputValue(value || '');
    }
  }, [value]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
  };

  const handlePickerChange = (date) => {
    if (date && !Number.isNaN(new Date(date).getTime())) {
      const iso = new Date(date).toISOString();
      const formatDate = format(new Date(date), 'dd.MM.yyyy');
      setInputValue(formatDate);
      onChange(iso);
    } else {
      setInputValue('');
      onChange('');
    }
    setPickerOpen(false);
  };

  const handleBlur = () => {
    const parsed = parse(inputValue, 'dd.MM.yyyy', new Date());

    if (isValid(parsed)) {
      onChange(parsed.toISOString());
    } else {
      onChange(inputValue);
    }
  };

  return (
    <>
      <TextField
        variant="standard"
        label={label}
        name={name}
        required={required}
        fullWidth
        inputRef={anchorRef}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        error={!isValidValue(inputValue)}
        helperText={!isValidValue(inputValue) ? 'Введите дату или выражение, например {СЕГОДНЯ}+1' : ''}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Выбрать дату из календаря">
                <IconButton onClick={() => setPickerOpen((prev) => !prev)}>
                  <InsertInvitationIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: '220px' }}
      />

      <InvisiblePopper open={pickerOpen} anchorEl={anchorRef.current} placement="bottom">
        <MUIDateTimePicker
          open
          value={ddMMyyyytoISO(inputValue)}
          minDateTime={from}
          minDate={from}
          onChange={handlePickerChange}
          onClose={() => setPickerOpen(false)}
          error={error}
          dateOnly={dateOnly}
          renderInput={(params) => (
            <InvisibleTextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
              }}
            />
          )}
        />
      </InvisiblePopper>
    </>
  );
}

AdvancedDatePicker.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  from: PropTypes.instanceOf(Date),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.bool,
  dateOnly: PropTypes.bool,
  allowExpressions: PropTypes.bool,
};

AdvancedDatePicker.defaultProps = {
  label: '',
  value: '',
  from: null,
  required: false,
  error: false,
  dateOnly: false,
  allowExpressions: false,
};

export default AdvancedDatePicker;
